import { MutableRefObject, DependencyList, useEffect } from 'react';
import { throttle } from 'lodash-unified';
import { ISize } from '../interfaces/ISize';

const THROTTLE_WAIT = 150;

export const useElementOnResize = <E extends HTMLElement>(
    containerRef: MutableRefObject<E> | null,
    callback: (size: ISize, entry: ResizeObserverEntry) => void,
    dependencies: DependencyList = [],
    throttleWait = THROTTLE_WAIT,
): void => {
    useEffect(() => {
        if (!containerRef?.current) {
            return undefined;
        }

        let resizeObserver: ResizeObserver;
        let handleSize: ReturnType<typeof throttle>;

        try {
            handleSize = throttle((entries: ResizeObserverEntry[]) => {
                const entry = entries[0];

                requestAnimationFrame(() => {
                    callback(
                        {
                            width: entry.borderBoxSize?.[0].inlineSize ?? entry.contentRect.width,
                            height: entry.borderBoxSize?.[0].blockSize ?? entry.contentRect.height,
                        },
                        entry,
                    );
                });
            }, throttleWait);

            resizeObserver = new ResizeObserver(handleSize);
            resizeObserver.observe(containerRef.current);
        } catch {
            console.warn('ResizeObserver failed.');
        }

        return () => {
            resizeObserver?.disconnect();
            handleSize?.cancel();
        };
    }, dependencies);
};
